import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QRCodeGenerator } from 'src/shared/components';
import {
  ImagesSection,
  GameExpiredOverlay,
  processFormData,
  MusicSection,
  FileDownloadSection,
  ActionsSection,
  ResultsSection,
  RealProgress,
  ExampleImagesSection,
} from 'src/shared/components/Constructor';
import { TextFieldConfig, ImageFieldConfig } from 'src/shared/components/Constructor/types';

import InfoIcon from '@mui/icons-material/Info';
import { Container, Typography, IconButton, Tooltip, Checkbox, FormControlLabel } from '@mui/material';

import MusicUploader from '@shared/components/MusicUploader';
import type { GameKey } from '@shared/types';
import { isGameExpired } from '@shared/utils/gameExpiration';

import { authContext } from '@store/index';

import * as ClientActions from './client.actions';
import { TOOLTIPS_CONTENT } from './client.constants';
import { FormInputs } from './client.types';
import { FormValues } from 'src/shared/components/Constructor/types';

import { getIsClientFormDisabled, validateFormData, formatFormDataForSubmission } from './client.utils';

import styles from './client.module.scss';

interface ClientPageProps {
  gameKey: GameKey;
}

// Конфигурация полей для ClientFlappy
const textFields: TextFieldConfig[] = [
  { 
    name: 'urlButton', 
    label: 'Текст Game Over', 
    placeholder: 'Введите текст для экрана проигрыша', 
    tooltip: TOOLTIPS_CONTENT.urlButton, 
    required: false 
  },
  { 
    name: 'textTitle', 
    label: 'Заголовок игры', 
    placeholder: 'Введите заголовок игры', 
    tooltip: TOOLTIPS_CONTENT.textTitle, 
    required: false 
  },
];

const imageFields: ImageFieldConfig[] = [
  { 
    name: 'imagePlayer', 
    label: 'Изображение птички', 
    aspectRatio: 1, 
    tooltip: TOOLTIPS_CONTENT.imagePlayer, 
    required: true,
    maskImage: '/src/pages/Constructors/ClientFlappy/circle-mask.svg'
  },
];

const ClientPageFlappy: FC<ClientPageProps> = ({ gameKey: initialGameKey }) => {
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }, []);

  const { t } = useTranslation();
  const [authData] = useContext(authContext);

  const [gameKey, setGameKey] = useState(initialGameKey);
  const { gameBuild: alreadyExistedGameBuild } = gameKey;
  const [setActivateGameDate, setActivateGameDateLoading] = ClientActions.useSetActivateGameDate();
  const gameDefaults = ClientActions.useFetchGameDefaults(gameKey.gameName);
  const [existingGameBuild, fetchGameBuild, gameBuildError, gameBuildLoading] = ClientActions.useFetchGameBuild(
    gameKey.gameBuildId
  );

  const [createdGameBuild, handleGameBuildSubmit, gameBuildCreationLoading] =
    ClientActions.useCreateGameBuild();
  const [trimmedAudioFile, setTrimmedAudioFile] = useState<File | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [stepProgress, setStepProgress] = useState(0);
  const croppedPixelsDataRef = useRef<{ [key: string]: any }>({});
  const [processedImages, setProcessedImages] = useState<{ [key: string]: File }>({});

  const gameBuild = createdGameBuild ?? existingGameBuild ?? alreadyExistedGameBuild;
  const showGameLink = Boolean(gameBuild && typeof gameBuild === 'object' && '_id' in gameBuild);

  // Проверка истекшей игры
  const [isGameExpiredState, setIsGameExpiredState] = useState(false);

  useEffect(() => {
    const expired = isGameExpired(gameKey.activateDate, gameKey.gameDayCount);
    setIsGameExpiredState(expired);
  }, [gameKey.activateDate, gameKey.gameDayCount]);

  // Определяем режим только для чтения
  const isReadOnly = authData?.isReadOnly || false;

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<FormInputs & Record<string, any>>({
    defaultValues: {
      language: 'ru',
      imagePlayer: null,
      urlButton: '',
      fileDownloadUrl: '',
      textTitle: '',
      enableCustomMusic: false,
      customMusicFile: null,
      waveformTrimmer: null,
    },
  });

  const watchedValues = watch();

  // Отладочная информация для watchedValues
  useEffect(() => {
    console.log('👀 watchedValues изменились:', watchedValues);
  }, [watchedValues]);

  // Загружаем существующие данные игры
  useEffect(() => {
    console.log('🔍 Loading game data:', {
      gameBuild,
      gameDefaults,
      existingGameBuild,
      createdGameBuild
    });

    if (gameBuild && typeof gameBuild === 'object' && '_id' in gameBuild) {
      console.log('📦 GameBuild data:', gameBuild);
      const newValues: FormInputs = {
        language: 'ru',
        imagePlayer: (gameBuild as any).imagePlayer || null,
        // Разделяем: urlButton для URL, fileDownloadUrl для файла
        // Используем пустые строки вместо null для избежания предупреждений React
        urlButton: (gameBuild as any).urlButton || '',
        fileDownloadUrl: (gameBuild as any).fileDownloadUrl || '',
        textTitle: (gameBuild as any).textTitle || '',
        enableCustomMusic: !!(gameBuild as any).musicUrl,
        customMusicFile: (gameBuild as any).musicUrl || null,
        waveformTrimmer: null,
      };
      
      console.log('🎵 Music URL:', {
        gameBuildMusicUrl: gameBuild.musicUrl,
        gameDefaultsMusicUrl: gameDefaults?.defaultMusicUrl,
        finalMusicUrl: newValues.customMusicFile
      });
      
      reset(newValues);
    } else if (gameDefaults) {
      // console.log('🎵 Using default music:', gameDefaults.defaultMusicUrl);
      reset({
        language: 'ru',
        imagePlayer: null,
        urlButton: '',
        fileDownloadUrl: '',
        textTitle: '',
        enableCustomMusic: !!gameDefaults.defaultMusicUrl,
        customMusicFile: gameDefaults.defaultMusicUrl || null,
        waveformTrimmer: null,
      });
    } else {
      console.log('🎵 Using fallback values (no gameDefaults)');
      reset({
        language: 'ru',
        imagePlayer: null,
        urlButton: '',
        fileDownloadUrl: '',
        textTitle: '',
        enableCustomMusic: false,
        customMusicFile: null,
        waveformTrimmer: null,
      });
    }
  }, [gameBuild, reset, gameDefaults, existingGameBuild, existingGameBuild?._id]);

  // Обновляем gameKey после успешного создания игры
  useEffect(() => {
    if (createdGameBuild) {
      const result = createdGameBuild;
      if (result && result._id && result._id !== gameKey.gameBuildId) {
        setGameKey((prev) => ({
          ...prev,
          gameBuild: result,
          gameBuildId: result._id,
        }));
      }

      if (!gameKey.activateDate) {
        setActivateGameDate(gameKey._id);
        setGameKey((prev) => ({
          ...prev,
          activateDate: new Date(),
        }));
      }
    }
  }, [createdGameBuild, setActivateGameDate, gameKey._id, gameKey.activateDate, gameKey.gameBuildId]);

  // Проверяем, отключена ли форма
  const isFormDisabled = getIsClientFormDisabled(
    gameBuildCreationLoading,
    gameBuildLoading,
    isReadOnly,
    isGameExpiredState
  );

  // Обработчик отправки формы
  const onSubmit = async (data: FormInputs) => {
    try {
      console.log('🚀 Начинаем создание игры с данными:', data);
      setShowProgress(true);
      setCurrentStep('validation');
      setStepProgress(10);

      // Валидация данных
      const validationErrors = validateFormData(data);
      console.log('🔍 Результаты валидации:', validationErrors);
      if (validationErrors.length > 0) {
        console.log('❌ Ошибки валидации:', validationErrors);
        validationErrors.forEach(error => {
          setError('root', { message: error });
        });
        setShowProgress(false);
        setStepProgress(0);
        return;
      }

      setStepProgress(100);
      setCurrentStep('music');
      setStepProgress(10);

      // Обработка музыки
      setStepProgress(50);
      if (data.enableCustomMusic && data.customMusicFile) {
        setStepProgress(100);
      }

      setCurrentStep('images');
      setStepProgress(10);

      // Обработка изображений
      setStepProgress(50);
      if (data.imagePlayer) {
        setStepProgress(100);
      }

      setCurrentStep('upload');
      setStepProgress(10);

      // Используем processFormData как в других конструкторах
      const formData = await processFormData(
        data,
        gameKey,
        trimmedAudioFile,
        {
          imageFields: imageFields.map(f => f.name),
          musicFields: ['customMusicFile'],
          fileFields: ['urlButton'],
          gameType: 'flappy',
        }
      );

      setStepProgress(100);
      setCurrentStep('creation');
      setStepProgress(10);

      // Отправляем данные на сервер
      const result = await handleGameBuildSubmit(formData);

      setStepProgress(100);
      setCurrentStep('complete');

        // Обновляем gameKey с новой сборкой
  if (result) {
    setGameKey(prev => ({
      ...prev,
      gameBuild: result,
      gameBuildId: result._id,
    }));
  }

  // Активируем игру если еще не активирована
  if (result && !gameKey.activateDate) {
    setActivateGameDate(gameKey._id);
    setGameKey(prev => ({
      ...prev,
      activateDate: new Date(),
    }));
  }

      // Завершение обрабатывается в RealProgress onComplete

    } catch (error: any) {
      console.error('Ошибка создания игры:', error);
      setError('root', { 
        message: error.response?.data?.message || 'Произошла ошибка при создании игры' 
      });
      setShowProgress(false);
      setStepProgress(0);
    }
  };

  // Обработчик тестирования игры
  const handleTestGame = () => {
    if (gameBuild) {
      window.open(`/game/flappy?buildId=${gameBuild._id}`, '_blank');
    }
  };

  // Обработчик ошибок формы
  const onError = (errors: any) => {
    const missingFields = [
      ...textFields.filter(f => f.required && errors[f.name]),
      ...imageFields.filter(f => f.required && errors[f.name]),
    ];

    if (missingFields.length > 0) {
      const fieldsList = missingFields.map(({ label }) => `📝 ${label}`).join('\n');
      const errorMessage = `Пожалуйста, заполните все обязательные поля:\n\n${fieldsList}`;
      setError('root', { 
        message: errorMessage
      });
    }
  };



  // Обработчик ошибок - игнорируем 404 ошибки (игра не существует)
  if (gameBuildError) {
    if (gameBuildError.includes('404') || gameBuildError.includes('не найден') || gameBuildError.includes('Not Found')) {
      console.log('ℹ️ Игра не существует (404) - это нормально для новой игры');
    } else {
      console.log('❌ Критическая ошибка загрузки игры:', gameBuildError);
    }
  }
  
  if (gameBuildError && !gameBuildError.includes('404') && !gameBuildError.includes('не найден') && !gameBuildError.includes('Not Found')) {
    return (
      <Container className={styles.clientFlappyContainer}>
        <div className={styles.errorMessage}>
          <Typography variant="h6" color="error">
            Ошибка загрузки игры
          </Typography>
          <Typography>{gameBuildError}</Typography>
        </div>
      </Container>
    );
  }

  // Проверка истекшей игры
  if (isGameExpiredState) {
    return <GameExpiredOverlay gameKey={gameKey} />;
  }

  return (
    <Container className={styles.clientFlappyContainer}>
      {/* Заголовок */}
      <div className={styles.header}>
        <Typography variant="h1">Конструктор Flappy Bird</Typography>
        <Typography variant="body1">
          Создайте свою уникальную игру Flappy Bird с кастомными изображениями и текстом
        </Typography>
      </div>

      {/* Секция с примерами изображений */}
      <ExampleImagesSection gameDefaults={gameDefaults} />

      {/* Форма */}
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {/* Секция изображения птички */}
        <div className={styles.formSection}>
          <Typography variant="h2">Изображение птички</Typography>
          <div className={styles.birdImageSection}>
            <ImagesSection
              images={imageFields}
              control={control}
              errors={errors}
              isReadOnly={isReadOnly}
              cropDataRef={croppedPixelsDataRef}
              gameType="flappy"

            />
          </div>
        </div>

        {/* Секция текстовых полей */}
        <div className={styles.formSection}>
          <Typography variant="h2">Текстовые настройки</Typography>
          <div className={styles.textFieldsSection}>
            <Controller
              name="urlButton"
              control={control}
              render={({ field }) => (
                <div className={styles.textField}>
                  <label>
                    Текст Game Over
                    <Tooltip title={TOOLTIPS_CONTENT.urlButton}>
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </label>
                  <input
                    {...field}
                    placeholder="Введите текст для экрана проигрыша"
                    disabled={isReadOnly}
                  />
                </div>
              )}
            />

            <Controller
              name="textTitle"
              control={control}
              render={({ field }) => (
                <div className={styles.textField}>
                  <label>
                    Заголовок игры
                    <Tooltip title={TOOLTIPS_CONTENT.textTitle}>
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </label>
                  <input
                    {...field}
                    placeholder="Введите заголовок игры"
                    disabled={isReadOnly}
                  />
                </div>
              )}
            />
          </div>
        </div>

        {/* Секция музыки */}
        <MusicSection
          control={control}
          errors={errors}
          isReadOnly={isReadOnly}
          setError={setError}
          onTrimmedFileChange={setTrimmedAudioFile}
          musicFieldName="customMusicFile"
          waveformTrimmerFieldName="waveformTrimmer"
        />

        {/* Секция загрузки файла */}
        <FileDownloadSection
          control={control}
          errors={errors}
          isReadOnly={isReadOnly}
          setError={setError}
          setValue={setValue}
          getValues={getValues}
          fieldName="urlButton"
        />

        {/* Секция действий */}
        <ActionsSection
          gameBuild={gameBuild}
          gameBuildCreationLoading={gameBuildCreationLoading}
          isReadOnly={isReadOnly}
          onTestGameClick={handleTestGame}
          hasErrors={Object.keys(errors).length > 0}
        />
      </form>

      {/* Секция результатов */}
      {showGameLink && gameBuild && (
        <ResultsSection 
          errors={errors}
          gameBuild={gameBuild} 
          showGameLink={showGameLink}
        />
      )}

      {/* QR-код компонент */}
      <QRCodeGenerator
        gameUrl={gameBuild ? `${window.location.origin}/game?buildId=${gameBuild._id}` : ''}
        gameBuildId={gameBuild?._id}
        showGameLink={showGameLink}
      />

      {/* Стандартный прогресс-бар создания игры */}
      <RealProgress
        isVisible={showProgress}
        currentStep={currentStep}
        stepProgress={stepProgress}
        onComplete={() => {
          setShowProgress(false);
          setStepProgress(0);
        }}
      />

      {/* Сообщения об ошибках */}
      {errors.root && (
        <div className={styles.errorMessage}>
          <Typography>{errors.root.message}</Typography>
        </div>
      )}
    </Container>
  );
};

export default ClientPageFlappy;
